import { CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./UtilityPhonePreview.module.scss";
import { BatteryIcon, MobileNetworkIcon, ThreeDotsIcon, WiFiIcon } from "../../../../../assets/icons";
import { AccountIcon, BellNotificationIcon } from "../../../../../assets/customermenu-icons";
import QuickServiceCard from "../../../../customer/dashboard/quick-services/quick-services-card/QuickServicesCard";
import { PrepaidEnergyProfileCards } from "../../../../customer/dashboard/meter-consumption/prepaid/PrepaidEnergyProfileBox";
import { quickServiceItems } from "../../../../customer/dashboard/quick-services/QuickServices.constants";
import { TxRow } from "../../../../customer/dashboard/recent-transactions/RecentTransactions";
import { TransactionCategory, TransactionItem, TRANSACTION_STATUS } from "../../../../../model/transaction";
import { menuItems } from "../../../../../layouts/customer-layout/customer-sidemenu/CustomerMenu.constants";

interface UtilityPhonePreviewProps {
	primaryColor: string;
	secondaryColor: string;
	accentColor: string;
	logo: File | string | null;
	acronym: string;
}

// The real customer tiles, unfiltered — this is a mockup, not a live tenant view,
// so feature flags and the energy profile don't apply.
const PREVIEW_SERVICES = quickServiceItems.slice(0, 8);

// Dummy rows in the real shape, so TxRow derives its own titles, icons and status colors.
const PREVIEW_TRANSACTIONS: TransactionItem[] = [
	{
		id: "preview-1",
		reference: "preview-1",
		externalReference: "",
		type: 1,
		category: TransactionCategory.VendingTransaction,
		status: TRANSACTION_STATUS.COMPLETED,
		createdAt: "2024-11-25T22:45:23Z",
		updatedAt: "2024-11-25T22:45:23Z",
		amount: { currency: "NGN", amount: 45200 },
	},
	{
		id: "preview-2",
		reference: "preview-2",
		externalReference: "",
		type: 1,
		category: TransactionCategory.BillingTransaction,
		status: TRANSACTION_STATUS.IN_PROGRESS,
		createdAt: "2024-11-24T09:12:04Z",
		updatedAt: "2024-11-24T09:12:04Z",
		amount: { currency: "NGN", amount: 56500 },
	},
];

const ACTIVE_TAB = "home";

const isHex = (value: string) => /^#[0-9a-fA-F]{6}$/.test(value);

/** Mock of the customer app, tinted with the brand colors being entered. */
export function UtilityPhonePreview({
	primaryColor,
	secondaryColor,
	accentColor,
	logo,
	acronym,
}: Readonly<UtilityPhonePreviewProps>) {
	const { t } = useTranslation();
	const [logoUrl, setLogoUrl] = useState<string | null>(null);

	useEffect(() => {
		if (!logo) {
			setLogoUrl(null);
			return;
		}

		// On edit the logo starts out as the URL it is already stored under.
		if (typeof logo === "string") {
			setLogoUrl(logo);
			return;
		}

		const url = URL.createObjectURL(logo);
		setLogoUrl(url);
		return () => URL.revokeObjectURL(url);
	}, [logo]);

	// Scoped brand tokens — the same custom properties applyBrand() writes on <html>,
	// but confined to this frame. Reused customer components (QuickServiceCard reads
	// --app-secondary) therefore paint in the utility being entered, not in the BPS
	// brand the surrounding admin portal runs on.
	// Half-typed hex would blank the preview, so fall back until the value is valid.
	const themeVars = {
		"--app-primary": isHex(primaryColor) ? primaryColor : "#2E3336",
		"--app-secondary": isHex(secondaryColor) ? secondaryColor : "#7F7D82",
		"--app-accent": isHex(accentColor) ? accentColor : "#FFB400",
	} as CSSProperties;

	return (
		<div className={styles.frame} style={themeVars}>
			<div className={styles.screen}>
				<div className={styles.top}>
					{/* Three-column row so the time and the indicators sit on the island's centerline. */}
					<div className={styles.statusBar}>
						<span className={styles.time}>9:41</span>
						<span className={styles.island} />
						<span className={styles.statusIcons}>
							<MobileNetworkIcon />
							<WiFiIcon/>
							<BatteryIcon />
						</span>
					</div>

					<div className={styles.brandRow}>
						<span className={styles.brandMark}>
							{logoUrl ? (
								<img src={logoUrl} alt="Utility logo" />
							) : (
								// Stand-in until a logo is uploaded: a dashed slot carrying the acronym being typed.
								<span className={styles.logoSlot}>{acronym.trim().slice(0, 4).toUpperCase()}</span>
							)}
						</span>
						{/* Mirrors the real mobile header (MobileCustomerTopHeader): notification pill, then avatar. */}
						<div className={styles.headerRight}>
							<span className={styles.notificationPill}>
								<span className={styles.notificationCount}>22</span>
								<BellNotificationIcon />
							</span>
							<span className={styles.avatar}>
								<AccountIcon />
							</span>
						</div>
					</div>

					{/* The real dashboard cards. Nulls throughout — the empty state is the mockup state. */}
					<div className={styles.energyCards}>
						<PrepaidEnergyProfileCards lastPurchaseUnits={null} consumptionKwh={null} unitRemaining={null} />
					</div>
				</div>

				<div className={styles.body}>
					<div className={styles.services}>
						{PREVIEW_SERVICES.map(service => (
							// No `link` — the tiles render as inert buttons inside the preview.
							<QuickServiceCard key={service.id} icon={service.icon} title={t(service.title)} />
						))}
						{/* Same trailing tile as the real grid, minus the modal it opens. */}
						<QuickServiceCard icon={<ThreeDotsIcon />} title={t("home.moreService")} />
					</div>

					<div className={styles.transactionsCard}>
						<div className={styles.sectionTitle}>
							<span>{t("common.labels.todaysTransactions")}</span>
							<span>{t("wallet.seeMore")}</span>
						</div>

						{/* The dashboard's own rows — inert here, so the click handler is a no-op. */}
						<div className={styles.transactions}>
							{PREVIEW_TRANSACTIONS.map(transaction => (
								<TxRow key={transaction.id} transaction={transaction} onClick={() => {}} />
							))}
						</div>
					</div>
				</div>

				{/* The customer menu's own items — unfiltered, so the Support flag doesn't apply here. */}
				<div className={styles.tabBar}>
					{menuItems.map(item => {
						const isActive = item.id === ACTIVE_TAB;
						return (
							<span key={item.id} className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}>
								<span className={styles.tabIcon}>{isActive ? item.activeIcon : item.icon}</span>
								{item.label}
							</span>
						);
					})}
				</div>
			</div>
		</div>
	);
}
